<template>
  <div class="h-full">
    <div
      class="flex flex-wrap sm:flex-nowrap items-center justify-between my-4 lg:my-8"
    >
      <!-- title -->
      <div class="flex-grow flex items-center">
        <svg-icon
          type="mdi"
          size="32"
          :path="icons.farmers"
          class="text-navy"
        />

        <span class="ml-2 text-navy font-bold leading-7 text-2xl">
          Farmers
        </span>
      </div>

      <!-- Back to Farmers -->
      <div class="sm:mr-3 mb-4">
        <inertia-link
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.farmer.index')"
        >
          <svg-icon
            type="mdi"
            size="20"
            :path="icons.back"
            class="text-white"
          />
          <span class="sr-only"> Farmers List </span>
        </inertia-link>
      </div>
      <div class="flex flex-no-wrap">
        <div>
          <a
            :href="route('app.farmer.bulk-upload.download', 'sample')"
            class="flex flex-col items-center justify-center ml-2 mr-5"
            target="_blank"
          >
            <div
              class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
            >
              <svg-icon
                type="mdi"
                size="20"
                :path="icons.upload"
                class="text-white"
              />
            </div>
            <div class="text-xs text-navy">Sample</div>
          </a>
        </div>

        <div>
          <a
            :href="route('app.farmer.bulk-upload.download', 'blank')"
            class="flex flex-col items-center justify-center"
            target="_blank"
          >
            <div
              class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
            >
              <svg-icon
                type="mdi"
                size="20"
                :path="icons.upload"
                class="text-white"
              />
            </div>
            <div class="text-xs text-navy">Blank</div>
          </a>
        </div>
      </div>
    </div>

    <flash-message />

    <div class="bg-white my-4 lg:my-8 mx-3 p-2">
      <h3 class="text-lg my-3">Instructions</h3>
      <p class="text-sm my-1.5">Download a blank spreadsheet to fill in.</p>
      <p class="text-sm my-1.5">
        The following fields are required: farmer, farm type.
      </p>
      <p class="text-sm my-1.5">
        The farm must be one of the following Dairy, Cattle Rearing, Cattle
        Other, Sheep, Tillage, Mixed Livestock
      </p>
      <p class="text-sm my-1.5">
        For Organic Status add Yes or No for the status.
      </p>
      <p class="text-sm my-1.5">
        Use the consultants email address in the consultants column.
      </p>
      <p class="text-sm my-1.5">
        The organisation name must match a valid organisation.
      </p>
    </div>

    <div class="bg-white my-4 lg:my-8 mx-3 lg:py-2">
      <form @submit.prevent="upload" enctype="multipart/form-data">
        <div class="flex flex-wrap w-full items-center">
          <div class="p-2 lg:mx-4 lg:w-1/2">
            <label class="block text-sm font-medium text-gray-700" for="bulk">
              Upload Bulk File
            </label>

            <input id="mobile" type="file" v-on:change="onChange" />
          </div>

          <div class="p-2 lg:mx-4">
            <button type="submit" class="btn-secondary">Upload file</button>
          </div>
        </div>
      </form>
    </div>

    <flash-message />

    <div v-if="error && errors.length">
      Row {{ errors.row }}

      <ul class="my-2 list-disc list-inside text-sm">
        <li v-for="(error, key) in errors.errors" :key="key">{{ error }}</li>
      </ul>

      <div class="my-4">
        <a
          class="text-purple-600"
          v-bind:href="'/app/farmer/bulk-upload/' + download + '/download'"
        >
          Click here to download your CSV file.
        </a>
        <span class="text-sm text-black"
          >Please review the errors above and upload the file again.</span
        >
      </div>
    </div>

    <div v-if="success" class="p-2 mb-2 font-bold bg-green-100 text-green-700">
      The upload has been successful
    </div>

    <div v-if="uploads.length > 0">
      <div
        class="overflow-x-auto shadow border-b border-gray-200 sm:rounded-lg"
      >
        <div v-if="uploads.length > 0">
          <table class="table-auto min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr class="text-left font-bold">
                <th
                  scope="col"
                  class="p-2 ptext-left text-xs text-gray-500 uppercase tracking-wider"
                >
                  Date
                </th>
                <th
                  scope="col"
                  class="p-2 ptext-left text-xs text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(upload, index) in uploads"
                :key="upload.id"
                :class="index % 2 == 0 ? 'bg-white' : 'bg-gray-50'"
              >
                <td
                  class="p-2 whitespace-nowrap text-sm text-gray-500 font-bold"
                >
                  {{ upload.date }}
                </td>
                <td class="p-2 whitespace-nowrap text-sm text-gray-500">
                  {{ upload.status }}
                </td>
                <td class="p-2 whitespace-nowrap text-sm text-gray-500">
                  <a
                    class="px-4 flex items-center text-blue-600"
                    v-bind:href="upload.download"
                  >
                    Download
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-center justify-center">
        <pagination class="mt-8 mb-4" :links="links" />
      </div>
    </div>

    <div
      v-if="uploads.length === 0"
      class="bg-white px-4 py-5 sm:rounded-lg sm:p-6"
    >
      <strong>No bulk uploads found.</strong>
    </div>
  </div>
</template>

<script>
import AppLayout from "@/Layouts/App";
import Pagination from "@/Components/Pagination";
import _ from "lodash";
import FlashMessage from "@/Components/FlashMessage.vue";
import { useToast } from "vue-toastification";
import { mdiHomeSiloOutline, mdiTrayArrowDown, mdiArrowLeft } from "@mdi/js";
export default {
  layout: [AppLayout],
  components: {
    Pagination,
  },

  props: {
    role: String,
    results: Object,
    paginate: String,
  },

  data() {
    return {
      uploads: this.results["original"]["data"],
      links: this.results["original"]["meta"]["pagination"],
      link: this.paginate,

      file: null,
      success: null,
      download: null,
      error: false,
      errors: [],
      toast: null,
      icons: {
        farmers: mdiHomeSiloOutline,
        upload: mdiTrayArrowDown,
        back: mdiArrowLeft,
      },
    };
  },

  mounted() {
    this.toast = useToast();
  },

  methods: {
    onChange(e) {
      this.file = e.target.files[0];
    },

    upload(e) {
      e.preventDefault();
      let existingObj = this;

      const config = {
        headers: {
          "content-type": "multipart/form-data",
        },
      };

      let data = new FormData();
      data.append("file", this.file);

      axios
        .post(route("app.farmer.bulk-upload.store"), data, config)
        .then(function (res) {
          existingObj.success = res.data.success;
          existingObj.errors = res.data.errors;
        })
        .catch(function (err) {
          existingObj.success = false;
          existingObj.error = true;
          existingObj.errors = err.response.data.errors;
          existingObj.download = err.response.data.download;
          err.response.data.errors.errors.forEach((single) => {
            existingObj.toast.info(single);
          });
        })
        .then(function (res) {
          existingObj.file = null;
          document.getElementById("mobile").value = null;
        });
    },
  },
};
</script>
